import { useCallback } from 'react';

import { DXF_MAPPING_ROLE, type DxfMappingRole } from '@frontend/api';
import {
  Badge,
  Box,
  Button,
  Divider,
  Empty,
  EmptyDescription,
  EmptyHeadline,
  Flex,
  Text,
  Tooltip,
  VStack,
  type VStackProps,
} from '@frontend/legacy-components';

import { usePanelRowSelect } from '../hooks/usePanelRowSelect';
import {
  MAPPING_ROLE_DEFINITIONS,
  IGNORE_ROLE_DEFINITION,
} from '../session/roles';
import type { PanelSelectionMode } from '../session/selectionModifiers';
import { selectablePanelRowStyles } from '../utils/panelRowStyles';

export type DxfMappingPanelProps = {
  /** Entity count per role. A role absent from the map has none. */
  counts: ReadonlyMap<DxfMappingRole, number>;
  /** False when the file carries no mapping — the empty state. */
  hasMapping: boolean;
  /** True while mapping mode is active, which changes what the panel lists. */
  isMapping: boolean;
  /** False when the file is locked, or no drawing has loaded. */
  canEnterMapping: boolean;
  /** When set, mapping cannot be started or edited. */
  lockReason: string | undefined;
  onEnterMappingMode: () => void;
  /**
   * Clicking a role selects its entities. Shift adds the role, or takes it back
   * out when all of it is already selected.
   */
  onSelectRole: (role: DxfMappingRole, mode: PanelSelectionMode) => void;
  /** Hovering a role previews what clicking it would take; `null` on leave. */
  onHoverRole: (role: DxfMappingRole | null) => void;
  /** Roles holding at least one entity that can actually be picked. */
  selectableRoles: ReadonlySet<DxfMappingRole>;
  /** Roles whose every selectable entity is selected. */
  selectedRoles: ReadonlySet<DxfMappingRole>;
} & VStackProps;

type DxfMappingRowProps = {
  role: DxfMappingRole;
  label: string;
  color: string;
  count: number | undefined;
  /** A role with nothing pickable in it has nothing to select, so it stays inert. */
  isSelectable: boolean;
  isSelected: boolean;
  onSelectRole: (role: DxfMappingRole, mode: PanelSelectionMode) => void;
  onHoverRole: (role: DxfMappingRole | null) => void;
  swatch: React.ReactNode;
};

const MappingCircle = ({
  color,
  opacity,
}: {
  color: string;
  opacity: number;
}) => {
  return (
    <Box
      flexShrink={0}
      boxSize={1.5}
      borderRadius="full"
      bg={color}
      opacity={opacity}
      aria-hidden
    />
  );
};
MappingCircle.displayName = 'MappingCircle';

const MappingSlash = ({
  color,
  opacity,
}: {
  color: string;
  opacity: number;
}) => (
  <Box
    flexShrink={0}
    boxSize={1.5}
    position="relative"
    opacity={opacity}
    aria-hidden
  >
    <Box
      position="absolute"
      left={0}
      right={0}
      top="50%"
      h="1.5px"
      bg={color}
      transform="rotate(-45deg)"
      transformOrigin="center"
    />
  </Box>
);
MappingSlash.displayName = 'MappingSlash';

const isRoleListedInPanel = (
  count: number | undefined,
  isMapping: boolean,
): boolean => isMapping || (count ?? 0) > 0;

/**
 * A single role row: a color swatch, the role's name, and how many entities
 * carry it. Deliberately shaped like `DxfLayerRow` — the two panels sit in the
 * same column and should read as one control surface.
 */
const DxfMappingRow = ({
  role,
  label,
  count,
  isSelectable,
  isSelected,
  onSelectRole,
  onHoverRole,
  swatch,
}: DxfMappingRowProps) => {
  const onSelect = useCallback(
    (mode: PanelSelectionMode) => onSelectRole(role, mode),
    [onSelectRole, role],
  );
  // The same handler the layers panel uses. A role row has no second action, so
  // no `onActivate` — the second click of a double-click is ignored.
  const handleSelect = usePanelRowSelect({
    isSelectable,
    isSelected,
    onSelect,
  });
  const handleHover = useCallback(() => onHoverRole(role), [onHoverRole, role]);
  const handleUnhover = useCallback(() => onHoverRole(null), [onHoverRole]);

  return (
    // Row chrome deliberately mirrors `DxfLayerRow`: the two panels sit in the same
    // column, so a hover or a selected state has to look the same in both or the
    // column reads as two unrelated controls. See `selectablePanelRowStyles`.
    <Flex
      as="button"
      type="button"
      aria-label={`Select all ${label} entities`}
      data-testid={`dxf-mapping-role-${role}`}
      align="center"
      px={1.5}
      py={0.75}
      gap={1.5}
      disabled={!isSelectable}
      {...selectablePanelRowStyles(isSelected, isSelectable)}
      onClick={handleSelect}
      onMouseEnter={isSelectable ? handleHover : undefined}
      onMouseLeave={isSelectable ? handleUnhover : undefined}
    >
      {swatch}
      <Text
        flex={1}
        minW={0}
        textAlign="left"
        fontSize="sm"
        fontWeight="medium"
        noOfLines={1}
      >
        {label}
      </Text>
      <Badge
        flexShrink={0}
        bg="page.plateMedium"
        borderColor="page.plateMedium"
        color="feature.secondary"
      >
        {count ?? 0}
      </Badge>
    </Flex>
  );
};

/**
 * The mapping panel, in its three states.
 *
 * With no mapping, an empty state offering to make one. While viewing a mapped
 * file, the roles actually in use with their counts. While mapping, the full set
 * of roles including the ones at zero — during a mapping pass the empty categories
 * are the point, since they are what the user is about to fill.
 */
export const DxfMappingPanel = ({
  counts,
  hasMapping,
  isMapping,
  canEnterMapping,
  lockReason,
  onEnterMappingMode,
  onSelectRole,
  onHoverRole,
  selectableRoles,
  selectedRoles,
  ...props
}: DxfMappingPanelProps) =>
  hasMapping ? (
    <VStack
      spacing={0}
      align="stretch"
      data-testid="dxf-mapping-panel"
      {...props}
    >
      {MAPPING_ROLE_DEFINITIONS.filter(
        ({ role }) =>
          role !== DXF_MAPPING_ROLE.IGNORE &&
          isRoleListedInPanel(counts.get(role), isMapping),
      ).map(({ role, label, color }) => (
        <DxfMappingRow
          key={role}
          role={role}
          label={label}
          color={color}
          count={counts.get(role)}
          isSelectable={selectableRoles.has(role)}
          isSelected={selectedRoles.has(role)}
          onSelectRole={onSelectRole}
          onHoverRole={onHoverRole}
          swatch={<MappingCircle color={color} opacity={1} />}
        />
      ))}
      {isRoleListedInPanel(
        counts.get(IGNORE_ROLE_DEFINITION.role),
        isMapping,
      ) && (
        <>
          <Divider borderColor="separator.divider" />
          <DxfMappingRow
            role={DXF_MAPPING_ROLE.IGNORE}
            label={IGNORE_ROLE_DEFINITION.label}
            color={IGNORE_ROLE_DEFINITION.color}
            count={counts.get(IGNORE_ROLE_DEFINITION.role)}
            isSelectable={selectableRoles.has(IGNORE_ROLE_DEFINITION.role)}
            isSelected={selectedRoles.has(IGNORE_ROLE_DEFINITION.role)}
            onSelectRole={onSelectRole}
            onHoverRole={onHoverRole}
            swatch={
              <MappingSlash color={IGNORE_ROLE_DEFINITION.color} opacity={1} />
            }
          />
        </>
      )}
    </VStack>
  ) : (
    <Empty size="sm" px={4} py={2} data-testid="dxf-mapping-empty">
      <EmptyHeadline>No mapping assigned</EmptyHeadline>
      <EmptyDescription>
        Assign cut, etch, and bend roles to this file’s geometry.
      </EmptyDescription>
      <Tooltip
        placement="top"
        label={lockReason}
        isDisabled={lockReason === undefined}
      >
        <Button
          size="sm"
          mt={1.5}
          aria-label="Add mapping"
          isDisabled={!canEnterMapping}
          onClick={onEnterMappingMode}
        >
          Add mapping
        </Button>
      </Tooltip>
    </Empty>
  );
DxfMappingPanel.displayName = 'DxfMappingPanel';
